/**
 * Hasta Uygulamasi - Email Dogrulama Sayfasi
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { Baby, Mail, Loader2, ArrowLeft, RefreshCw, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientVerifyPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const inputRefs = useRef([]);

  const getToken = () => localStorage.getItem('patient_token');
  const userStr = localStorage.getItem('patient_user');
  const user = userStr ? JSON.parse(userStr) : null;

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/patient');
      return;
    }
    // Eger zaten dogrulanmissa dashboard'a yonlendir
    if (user?.email_verified) {
      navigate('/patient/dashboard');
    }
  }, [navigate, user?.email_verified]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      // Paste durumu
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...code];
      digits.forEach((d, i) => {
        if (index + i < 6) newCode[index + i] = d;
      });
      setCode(newCode);
      const nextIdx = Math.min(index + digits.length, 5);
      inputRefs.current[nextIdx]?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      toast.error('Lutfen 6 haneli kodu girin');
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/patient-app/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: fullCode })
      });

      const data = await res.json();

      if (res.ok) {
        setVerified(true);
        // localStorage'daki user bilgisini guncelle
        if (user) {
          const updatedUser = { ...user, email_verified: true };
          localStorage.setItem('patient_user', JSON.stringify(updatedUser));
        }
        toast.success(data.message || 'Email dogrulandi!');
        setTimeout(() => {
          navigate('/patient/dashboard');
        }, 1500);
      } else {
        toast.error(data.detail || 'Dogrulama hatasi');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error('Baglanti hatasi');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/patient-app/resend-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Dogrulama kodu tekrar gonderildi');
      } else {
        toast.error(data.detail || 'Gonderme hatasi');
      }
    } catch (error) {
      toast.error('Baglanti hatasi');
    } finally {
      setResending(false);
    }
  };

  // Dogrulama sonrasi
  if (verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Email Dogrulandi!</h2>
            <p className="text-sm text-slate-400">Dashboard'a yonlendiriliyorsunuz...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="verify-email-card">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Baby className="w-8 h-8 text-pink-500" />
            <span className="font-bold text-xl text-slate-800">BabyFace AI</span>
          </div>
          <div className="w-14 h-14 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Mail className="w-7 h-7 text-pink-500" />
          </div>
          <CardTitle>Email Dogrulama</CardTitle>
          <CardDescription>
            <span className="font-medium text-slate-700">{user?.email}</span> adresine 6 haneli dogrulama kodu gonderdik
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* 6 haneli kod girisi */}
          <div className="flex justify-center gap-2 mb-6" data-testid="verification-code-inputs">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-200 transition-all"
                data-testid={`code-input-${index}`}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <Button
            className="w-full bg-pink-500 hover:bg-pink-600 mb-4"
            onClick={handleVerify}
            disabled={loading || code.join('').length !== 6}
            data-testid="verify-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Dogrula
          </Button>

          <div className="text-center space-y-3">
            <p className="text-sm text-slate-500">
              Kodu almadin mi?
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resending}
              data-testid="resend-code-btn"
            >
              {resending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Tekrar Gonder
            </Button>

            <div>
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('patient_token');
                  localStorage.removeItem('patient_user');
                  navigate('/patient');
                }}
                className="text-sm text-slate-400 hover:text-slate-600"
              >
                <ArrowLeft className="w-3 h-3 inline mr-1" />
                Girise Don
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientVerifyPage;
